import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { DropResult } from "react-beautiful-dnd";
import { DraggableList } from "../../components/draggable-list/DraggableList";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Colors } from "../../Colors";
import { RootState, useAppDispatch } from "../../store";
import { fetchInsurances, updateByPassAuthorization, updateInsuranceAuthPriority, updateNoCancelFeeInsurances } from "../../slices/InsurancesSlice";
import { updateAgentThunk } from "../../slices/AgentSlice";
import { AgentType, AuthorizationSource, Insurance, InsuranceType, WEBPT_INSURANCE_TYPES } from "../../types";
import { bodyCellSx, sectionCardSx, tableHeadCellSx } from "./payersStyles";
import { PrimaryButton } from "../../subcomponents/CustomButton";
import { useSnackbar } from "../../providers/SnackbarProvider";

const AUTH_SOURCE_OPTIONS: AuthorizationSource[] = ["prescription", "authorization", "policy"];
const SYSTEM_DEFAULT_AUTH_PRIORITY: AuthorizationSource[] = ["prescription", "authorization", "policy"];

interface InsuranceAuthPriorityCellProps {
  value: AuthorizationSource[] | undefined;
  typeValue: AuthorizationSource[] | undefined;
  onSave: (newValue: AuthorizationSource[]) => void;
}

const InsuranceAuthPriorityCell: React.FC<InsuranceAuthPriorityCellProps> = ({ value, typeValue, onSave }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draft, setDraft] = useState<{ source: AuthorizationSource; enabled: boolean }[]>([]);

  const isDefault = value === undefined || value.length === 0;

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    const base = !isDefault ? value! : typeValue && typeValue.length > 0 ? typeValue : SYSTEM_DEFAULT_AUTH_PRIORITY;
    const activeSet = new Set(base);
    const activeItems = base.map((s) => ({ source: s, enabled: true }));
    const inactiveItems = AUTH_SOURCE_OPTIONS.filter((s) => !activeSet.has(s)).map((s) => ({ source: s, enabled: false }));
    setDraft([...activeItems, ...inactiveItems]);
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSave = () => {
    onSave(draft.filter((d) => d.enabled).map((d) => d.source) as AuthorizationSource[]);
    handleClose();
  };

  const handleUnset = () => {
    onSave([]);
    handleClose();
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const reordered = [...draft];
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setDraft(reordered);
  };

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, overflow: "hidden", "&:hover .edit-btn": { opacity: 1 } }}>
        {isDefault ? (
          (() => {
            const inherited = typeValue && typeValue.length > 0 ? typeValue : SYSTEM_DEFAULT_AUTH_PRIORITY;
            const label = typeValue && typeValue.length > 0 ? "from type" : "default";
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap", overflow: "hidden" }}>
                {inherited.map((s, i) => (
                  <>
                    <Tooltip key={s} title={`${i === 0 ? "Highest" : i === inherited.length - 1 ? "Lowest" : ""} priority (${label})`} placement="top">
                      <Chip
                        label={s}
                        size="small"
                        sx={{
                          fontSize: "0.7rem",
                          height: "20px",
                          backgroundColor: "rgba(255, 255, 255, 0.04)",
                          color: Colors.info,
                          border: "1px solid rgba(255, 255, 255, 0.08)",
                          opacity: 0.75,
                          flexShrink: 0,
                        }}
                      />
                    </Tooltip>
                    {i < inherited.length - 1 && (
                      <Typography key={`arrow-${i}`} sx={{ fontSize: "0.6rem", color: Colors.info, opacity: 0.5, lineHeight: 1, flexShrink: 0 }}>
                        ›
                      </Typography>
                    )}
                  </>
                ))}
                <Typography sx={{ fontSize: "0.65rem", color: Colors.info, fontStyle: "italic", opacity: 0.6, ml: 0.25, flexShrink: 0 }}>{label}</Typography>
              </Box>
            );
          })()
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap", overflow: "hidden" }}>
            {value!.map((s, i) => (
              <>
                <Tooltip key={s} title={i === 0 ? "Highest priority" : i === value!.length - 1 ? "Lowest priority" : ""} placement="top">
                  <Chip
                    label={s}
                    size="small"
                    sx={{
                      fontSize: "0.7rem",
                      height: "20px",
                      backgroundColor: i === 0 ? `${Colors.primary}22` : "rgba(255, 255, 255, 0.06)",
                      color: i === 0 ? Colors.primary : "#FFF",
                      border: `1px solid ${i === 0 ? `${Colors.primary}44` : "rgba(255, 255, 255, 0.12)"}`,
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
                {i < value!.length - 1 && (
                  <Typography key={`arrow-${i}`} sx={{ fontSize: "0.6rem", color: Colors.info, lineHeight: 1, flexShrink: 0 }}>
                    ›
                  </Typography>
                )}
              </>
            ))}
          </Box>
        )}
        <IconButton className="edit-btn" onClick={handleOpen} size="small" sx={{ opacity: 0, transition: "opacity 0.15s", color: Colors.info, flexShrink: 0, "&:hover": { color: Colors.primary } }}>
          <EditIcon sx={{ fontSize: "14px" }} />
        </IconButton>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            backgroundColor: "#1e1e2e",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            borderRadius: "10px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            minWidth: 260,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: Colors.info, textTransform: "uppercase", letterSpacing: "0.5px", mb: 0.25 }}>Authorization Source Priority</Typography>
          <Typography sx={{ fontSize: "0.7rem", color: Colors.info, mb: 1.5 }}>Drag to reorder — top item has highest priority</Typography>
          <DraggableList
            items={draft}
            onDragEnd={handleDragEnd}
            getItemId={(item) => item.source}
            droppableId="insurance-auth-source-priority"
            sx={{ minHeight: "unset" }}
            renderItem={(item) => (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.75, px: 0.5, borderRadius: "6px", "&:hover": { backgroundColor: "rgba(255,255,255,0.04)" } }}>
                <DragIndicatorIcon sx={{ fontSize: "16px", color: "rgba(255,255,255,0.3)" }} />
                <Checkbox
                  checked={item.enabled}
                  onChange={() => setDraft((prev) => prev.map((d) => (d.source === item.source ? { ...d, enabled: !d.enabled } : d)))}
                  size="small"
                  sx={{ p: 0, color: "rgba(255,255,255,0.3)", "&.Mui-checked": { color: Colors.primary } }}
                />
                <Typography sx={{ fontSize: "0.813rem", color: item.enabled ? "#FFF" : Colors.info }}>{item.source}</Typography>
              </Box>
            )}
          />
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.5 }}>
            <Tooltip title="Remove override and use the insurance type's priority" placement="top">
              <Button onClick={handleUnset} size="small" sx={{ color: Colors.info, fontSize: "0.75rem", textTransform: "none" }}>
                Reset to type default
              </Button>
            </Tooltip>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={handleClose} size="small" sx={{ color: Colors.info, fontSize: "0.75rem" }}>
                Cancel
              </Button>
              <Button onClick={handleSave} size="small" variant="contained" sx={{ fontSize: "0.75rem" }}>
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

type FlagKind = "byPass" | "noCancelFee";

const FLAG_TOOLTIPS: Record<FlagKind | "singleBook", string> = {
  singleBook: "Patients with these plans require one-on-one appointments and will not be double booked.",
  byPass: "Patients with these insurances will be scheduled without checking for authorization.",
  noCancelFee: "Patients with these insurances will not be charged a cancel fee.",
};

const checkboxSx = {
  p: 0.5,
  color: "rgba(255, 255, 255, 0.3)",
  "&.Mui-checked": { color: Colors.primary },
  "&.Mui-disabled": { color: "rgba(255, 255, 255, 0.15)" },
};

export default function AllPayers() {
  const dispatch = useAppDispatch();
  const { showMessage } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth.user);
  const { insurances, loading: insurancesLoading } = useSelector((state: RootState) => state.insurances);
  const { agents } = useSelector((state: RootState) => state.agents);

  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [payerTypes, setPayerTypes] = useState<InsuranceType[]>([]);

  const [singleBookOverrides, setSingleBookOverrides] = useState(new Map<string, boolean>());
  const [byPassOverrides, setByPassOverrides] = useState(new Map<string, boolean>());
  const [noCancelFeeOverrides, setNoCancelFeeOverrides] = useState(new Map<string, boolean>());
  const [isLoading, setIsLoading] = useState(false);

  const isDirty = singleBookOverrides.size > 0 || byPassOverrides.size > 0 || noCancelFeeOverrides.size > 0;

  useEffect(() => {
    if (user?.token && !insurances.length) {
      dispatch(fetchInsurances(user.token));
    }
  }, [dispatch, user?.token, insurances.length]);

  useEffect(() => {
    if (!user?.token) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/payers/types`, { headers: { Authorization: `Bearer ${user.token}` } }).then((res) => setPayerTypes(res.data as InsuranceType[]));
  }, [user?.token]);

  const typeByEhrId = useMemo(() => {
    const map = new Map<string, InsuranceType>();
    payerTypes.forEach((t) => map.set(t.ehrInsuranceTypeId, t));
    return map;
  }, [payerTypes]);

  const agent = useMemo(() => agents.find((a) => a.agentType === AgentType.waitlist), [agents]);

  const singleBookIds = useMemo(() => new Set(agent?.singleBookInsuranceIds ?? []), [agent]);

  const filteredPayers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return insurances.filter((p) => {
      if (typeFilter !== "all" && p.ehrInsuranceTypeId !== typeFilter) {
        return false;
      }
      if (!q) return true;
      const typeLabel = p.ehrInsuranceTypeId ? (WEBPT_INSURANCE_TYPES[p.ehrInsuranceTypeId as keyof typeof WEBPT_INSURANCE_TYPES] ?? "") : "";
      return p.name?.toLowerCase().includes(q) || p.ehrInsuranceId?.toLowerCase().includes(q) || typeLabel.toLowerCase().includes(q);
    });
  }, [insurances, searchQuery, typeFilter]);

  const paginatedPayers = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPayers.slice(start, start + rowsPerPage);
  }, [filteredPayers, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, typeFilter]);

  const typeFilterOptions = useMemo(() => {
    const used = new Set<string>();
    insurances.forEach((p) => {
      if (p.ehrInsuranceTypeId) used.add(p.ehrInsuranceTypeId);
    });
    return Array.from(used).sort((a, b) => parseInt(a) - parseInt(b));
  }, [insurances]);

  const toggleLocalSingleBook = (id: string, checked: boolean) => {
    setSingleBookOverrides((prev) => {
      const next = new Map(prev);
      if (checked === singleBookIds.has(id)) {
        next.delete(id);
      } else {
        next.set(id, checked);
      }
      return next;
    });
  };

  const toggleLocalFlag = (kind: FlagKind, payer: Insurance, checked: boolean) => {
    const original = kind === "byPass" ? !!payer.byPassAuthorization : !!payer.noCancelFee;
    const setter = kind === "byPass" ? setByPassOverrides : setNoCancelFeeOverrides;
    setter((prev) => {
      const next = new Map(prev);
      if (checked === original) {
        next.delete(payer.insuranceId);
      } else {
        next.set(payer.insuranceId, checked);
      }
      return next;
    });
  };

  const handleSave = async () => {
    if (!user?.token) return;
    setIsLoading(true);
    try {
      if (singleBookOverrides.size > 0 && agent) {
        const next = new Set(singleBookIds);
        singleBookOverrides.forEach((checked, id) => {
          if (checked) next.add(id);
          else next.delete(id);
        });
        await dispatch(
          updateAgentThunk({
            token: user.token,
            agent: { agentId: agent.agentId, singleBookInsuranceIds: [...next] },
          }),
        ).unwrap();
      }
      if (byPassOverrides.size > 0) {
        const nextIds = insurances.filter((i) => (byPassOverrides.has(i.insuranceId) ? byPassOverrides.get(i.insuranceId) : i.byPassAuthorization)).map((i) => i.insuranceId);
        await dispatch(updateByPassAuthorization({ token: user.token, insuranceIds: nextIds })).unwrap();
      }
      if (noCancelFeeOverrides.size > 0) {
        const nextIds = insurances.filter((i) => (noCancelFeeOverrides.has(i.insuranceId) ? noCancelFeeOverrides.get(i.insuranceId) : i.noCancelFee)).map((i) => i.insuranceId);
        await dispatch(updateNoCancelFeeInsurances({ token: user.token, insuranceIds: nextIds })).unwrap();
      }
      setSingleBookOverrides(new Map());
      setByPassOverrides(new Map());
      setNoCancelFeeOverrides(new Map());
      showMessage("Payer settings updated successfully.", "success");
    } catch (error) {
      console.error("Failed to update payer settings:", error);
      showMessage("Failed to update payer settings.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscard = () => {
    setSingleBookOverrides(new Map());
    setByPassOverrides(new Map());
    setNoCancelFeeOverrides(new Map());
  };

  const showLoadingPayers = insurancesLoading && insurances.length === 0;

  return (
    <>
      <Paper sx={sectionCardSx}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#FFF" }}>All Payers</Typography>
            <Typography sx={{ fontSize: "0.75rem", color: Colors.info, mt: 0.25 }}>
              {filteredPayers.length.toLocaleString()} {filteredPayers.length === 1 ? "payer" : "payers"}
              {filteredPayers.length !== insurances.length && <> of {insurances.length.toLocaleString()}</>}
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <TextField
              placeholder="Search by name, ID, or type…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: Colors.info, fontSize: "18px" }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery("")} sx={{ color: Colors.info }}>
                      <CloseIcon sx={{ fontSize: "16px" }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                minWidth: "260px",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  color: "#FFF",
                  fontSize: "0.813rem",
                  "& fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.12)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: Colors.primary,
                    borderWidth: "1px",
                  },
                },
              }}
            />

            <TextField
              select
              size="small"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              SelectProps={{
                IconComponent: FilterListIcon,
                MenuProps: {
                  PaperProps: {
                    sx: {
                      backgroundColor: Colors.bg3,
                      color: "#FFF",
                      maxHeight: 360,
                    },
                  },
                },
              }}
              sx={{
                minWidth: "160px",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "rgba(255, 255, 255, 0.02)",
                  color: "#FFF",
                  fontSize: "0.813rem",
                  "& fieldset": {
                    borderColor: typeFilter !== "all" ? Colors.primary : "rgba(255, 255, 255, 0.12)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: Colors.primary,
                  },
                },
                "& .MuiSelect-icon": {
                  color: Colors.info,
                  fontSize: "18px",
                  right: 10,
                },
              }}
            >
              <MenuItem value="all">All Types</MenuItem>
              {typeFilterOptions.map((id) => (
                <MenuItem key={id} value={id}>
                  {WEBPT_INSURANCE_TYPES[id as keyof typeof WEBPT_INSURANCE_TYPES] ?? `Type ${id}`}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...tableHeadCellSx, pl: 3, width: "140px" }}>EHR ID</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: "160px" }}>Type</TableCell>
                <TableCell sx={tableHeadCellSx}>Name</TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: "100px", textAlign: "center" }}>
                  <Tooltip title={FLAG_TOOLTIPS.singleBook} placement="top">
                    <span>1:1 Appts</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: "100px", textAlign: "center" }}>
                  <Tooltip title={FLAG_TOOLTIPS.byPass} placement="top">
                    <span>Ignore Visit Restrictions</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...tableHeadCellSx, width: "110px", textAlign: "center" }}>
                  <Tooltip title={FLAG_TOOLTIPS.noCancelFee} placement="top">
                    <span>No Cancel Fee</span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ ...tableHeadCellSx, pr: 3, width: "320px" }}>Approval Source Priority</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {showLoadingPayers ? (
                Array.from({ length: 6 }).map((_, idx) => (
                  <TableRow key={idx}>
                    {Array.from({ length: 7 }).map((__, cellIdx) => (
                      <TableCell
                        key={cellIdx}
                        sx={{
                          ...bodyCellSx,
                          ...(cellIdx === 0 ? { pl: 3 } : {}),
                          ...(cellIdx === 6 ? { pr: 3 } : {}),
                        }}
                      >
                        <Skeleton variant="text" sx={{ bgcolor: "rgba(255, 255, 255, 0.08)" }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginatedPayers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: 0, py: 6 }}>
                    <Box
                      sx={{
                        textAlign: "center",
                        color: Colors.info,
                      }}
                    >
                      <SearchIcon sx={{ fontSize: "40px", opacity: 0.2, mb: 1.5 }} />
                      <Typography
                        sx={{
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: "#FFF",
                          mb: 0.5,
                        }}
                      >
                        No payers found
                      </Typography>
                      <Typography sx={{ fontSize: "0.813rem" }}>{searchQuery || typeFilter !== "all" ? "Try adjusting your search or filters." : "Sync from your EHR to populate payers."}</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedPayers.map((payer) => {
                  const typeLabel = payer.ehrInsuranceTypeId ? WEBPT_INSURANCE_TYPES[payer.ehrInsuranceTypeId as keyof typeof WEBPT_INSURANCE_TYPES] : null;
                  return (
                    <TableRow
                      key={payer.insuranceId}
                      sx={{
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.02)",
                        },
                      }}
                    >
                      <TableCell sx={{ ...bodyCellSx, pl: 3 }}>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            color: Colors.info,
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                          }}
                        >
                          {payer.ehrInsuranceId}
                        </Typography>
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        {typeLabel ? (
                          <Chip
                            label={typeLabel}
                            size="small"
                            sx={{
                              backgroundColor: `${Colors.primary}1A`,
                              color: Colors.primary,
                              border: `1px solid ${Colors.primary}33`,
                              fontSize: "0.7rem",
                              height: "20px",
                            }}
                          />
                        ) : (
                          <Typography sx={{ fontSize: "0.75rem", color: Colors.info }}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={bodyCellSx}>
                        <Tooltip title={payer.name} placement="top-start">
                          <Typography
                            sx={{
                              fontSize: "0.813rem",
                              color: "#FFF",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "420px",
                            }}
                          >
                            {payer.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                        <Checkbox
                          size="small"
                          checked={singleBookOverrides.has(payer.insuranceId) ? singleBookOverrides.get(payer.insuranceId)! : singleBookIds.has(payer.insuranceId)}
                          onChange={(_, checked) => toggleLocalSingleBook(payer.insuranceId, checked)}
                          disabled={isLoading || !agent}
                          sx={checkboxSx}
                        />
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                        <Checkbox
                          size="small"
                          checked={byPassOverrides.has(payer.insuranceId) ? byPassOverrides.get(payer.insuranceId)! : !!payer.byPassAuthorization}
                          onChange={(_, checked) => toggleLocalFlag("byPass", payer, checked)}
                          disabled={isLoading}
                          sx={checkboxSx}
                        />
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, textAlign: "center" }}>
                        <Checkbox
                          size="small"
                          checked={noCancelFeeOverrides.has(payer.insuranceId) ? noCancelFeeOverrides.get(payer.insuranceId)! : !!payer.noCancelFee}
                          onChange={(_, checked) => toggleLocalFlag("noCancelFee", payer, checked)}
                          disabled={isLoading}
                          sx={checkboxSx}
                        />
                      </TableCell>
                      <TableCell sx={{ ...bodyCellSx, pr: 3 }}>
                        <InsuranceAuthPriorityCell
                          value={payer.authorizationSourcePriority}
                          typeValue={payer.ehrInsuranceTypeId ? typeByEhrId.get(payer.ehrInsuranceTypeId)?.authorizationSourcePriority : undefined}
                          onSave={(newValue) => {
                            if (!user?.token) return;
                            dispatch(updateInsuranceAuthPriority({ token: user.token, insuranceId: payer.insuranceId, authorizationSourcePriority: newValue }));
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredPayers.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 250]}
          sx={{
            color: "#FFF",
            borderTop: "1px solid rgba(255, 255, 255, 0.08)",
            "& .MuiTablePagination-select": { color: "#FFF" },
            "& .MuiTablePagination-selectIcon": { color: "#FFF" },
            "& .MuiTablePagination-displayedRows": { color: Colors.info },
            "& .MuiTablePagination-selectLabel": { color: Colors.info },
            "& .MuiIconButton-root": { color: "#FFF" },
            "& .MuiIconButton-root.Mui-disabled": {
              color: "rgba(255, 255, 255, 0.2)",
            },
          }}
        />
      </Paper>
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
        {isDirty && (
          <PrimaryButton
            onClick={handleDiscard}
            disabled={isLoading}
            sx={{
              backgroundColor: "transparent",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              color: "#FFF",
              "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.05)" },
            }}
          >
            Discard
          </PrimaryButton>
        )}
        <PrimaryButton onClick={handleSave} disabled={!isDirty || isLoading}>
          {isLoading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
        </PrimaryButton>
      </Box>
    </>
  );
}
